import { useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useT } from "@/hooks/useT";
import { Sparkles, Send, Loader2, Layers, Coins } from "lucide-react";

const SUGGESTIONS = [
  "Master derivatives for JEE Advanced",
  "Learn REST API design from scratch",
  "Crash course on macro-economics",
  "Intro to Quantum Mechanics for beginners",
];

const TIERS = [
  { id: "basic", label: "Basic", blurb: "Real-world analogies", credits: 1 },
  { id: "intermediate", label: "Intermediate", blurb: "Balanced depth", credits: 2 },
  { id: "expert", label: "Advanced", blurb: "Executive Summary + ROI", credits: 3 },
];

export default function CounselorChat({ onNewPath }) {
  const { t } = useT();
  const [goal, setGoal] = useState("");
  const [tier, setTier] = useState("intermediate");
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);

  const submit = async (text) => {
    const g = (text ?? goal).trim();
    if (!g) return;
    setLoading(true);
    try {
      const { data } = await api.post("/counselor/path", { goal: g, difficulty_tier: tier });
      onNewPath?.(data);
      setGoal("");
      const tierMeta = TIERS.find((tt) => tt.id === tier) || TIERS[1];
      toast.success(`${tierMeta.label} path generated · −${tierMeta.credits} credit${tierMeta.credits === 1 ? "" : "s"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI counselor failed");
    } finally {
      setLoading(false);
    }
  };

  // Mirrors Hub.jsx: chips populate the input and focus it so the user
  // can tweak or hit Generate deliberately. Previously the chips
  // auto-submitted and got blocked by `disabled={loading}` after the
  // first click, which made subsequent chips look dead.
  const handleSuggestionClick = (q) => {
    setGoal(q);
    inputRef.current?.focus();
  };

  return (
    <section className="glass p-6 md:p-8 relative overflow-hidden" data-testid="counselor-panel">
      <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-[#0044FF] opacity-[0.12] blur-3xl" />
      <div className="absolute -bottom-24 -left-24 w-72 h-72 rounded-full bg-[#FF7B00] opacity-[0.10] blur-3xl" />

      <div className="flex items-center gap-2 label-eyebrow mb-3">
        <Sparkles size={12} className="text-[#00FF9D]" /> {t("nav_academy")} · 30-minute modules
      </div>
      <h1 className="font-display text-3xl md:text-5xl tracking-tight leading-[1.05] max-w-3xl">
        What do you want to <span className="italic text-[#00FF9D]">master</span> today?
      </h1>
      <p className="text-white/55 mt-3 max-w-xl text-[15px]">
        Name a goal. Oak AI Campus returns a 5-step path you can open, explore, and stress-test with adaptive exams.
      </p>

      {/* Input + Generate button — mirrors Hub.jsx responsive stack */}
      <form
        onSubmit={(e) => { e.preventDefault(); submit(); }}
        className="mt-6 max-w-3xl"
      >
        <div className="flex flex-col md:flex-row md:items-stretch gap-2">
          <input
            ref={inputRef}
            value={goal}
            onChange={(e) => setGoal(e.target.value)}
            placeholder='e.g. "Master derivatives for JEE Advanced"'
            className="flex-1 bg-black/40 border border-white/10 rounded-xl px-4 py-3.5 text-[15px] placeholder:text-white/30 focus:outline-none focus:border-[#0044FF]/60 focus:ring-2 focus:ring-[#0044FF]/20 transition w-full"
            data-testid="chat-input-goal"
            disabled={loading}
          />
          <button
            type="submit"
            disabled={loading || !goal.trim()}
            className="btn-primary rounded-xl px-5 py-3.5 font-display font-semibold flex items-center justify-center gap-2 disabled:opacity-50 w-full md:w-auto"
            data-testid="btn-generate-path"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            {loading ? "Thinking…" : t("btn_generate")}
          </button>
        </div>
      </form>

      <div className="mt-5 flex flex-wrap gap-2" data-testid="academy-suggestions">
        {SUGGESTIONS.map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => handleSuggestionClick(s)}
            className="text-xs px-3 py-1.5 rounded-full border border-white/10 bg-white/5 hover:bg-white/10 text-white/70 hover:text-white transition"
            data-testid={`academy-suggestion-${s.slice(0, 12).replace(/\s+/g, "-").toLowerCase()}`}
          >
            {s}
          </button>
        ))}
      </div>

      {/* B1 — Mastery Track tier selector. Drives prompt depth (Basic /
          Intermediate / Expert with Executive Summary + Strategic ROI).
          iter20: every option is now styled as a clearly-clickable pill
          with its own accent color so the unselected ones don't look
          like decoration. */}
      <div
        className="mt-5 inline-flex items-center gap-2 rounded-2xl border border-white/10 bg-white/[0.03] p-1.5 flex-wrap"
        role="tablist"
        aria-label="Mastery track tier"
        data-testid="mastery-tier-selector"
      >
        <Layers size={12} className="text-white/40 ml-2" />
        {TIERS.map((tt) => (
          <button
            key={tt.id}
            type="button"
            role="tab"
            aria-selected={tier === tt.id}
            onClick={() => setTier(tt.id)}
            disabled={loading}
            className={`relative z-10 cursor-pointer px-3 py-1.5 rounded-xl text-xs font-display font-semibold transition flex flex-col items-start text-left min-w-[120px] disabled:opacity-50 disabled:cursor-not-allowed ${
              tier === tt.id
                ? "bg-[#0044FF]/25 text-white border border-[#0044FF]/55 shadow-[0_4px_20px_-8px_rgba(0,68,255,0.5)]"
                : "bg-white/[0.04] text-white/80 hover:text-white hover:bg-white/[0.08] border border-white/10 hover:border-white/25"
            }`}
            data-testid={`mastery-tier-${tt.id}`}
          >
            <div className="flex items-center justify-between w-full gap-2">
              <span>{tt.label}</span>
              <span
                className="inline-flex items-center gap-0.5 text-[9px] font-mono-oak bg-white/10 text-white/65 rounded-full px-1.5 py-0.5 tabular-nums"
                data-testid={`mastery-tier-${tt.id}-cost`}
              >
                <Coins size={8} /> {tt.credits}
              </span>
            </div>
            <span className="text-[9px] text-white/55 font-mono-oak normal-case tracking-normal mt-0.5">
              {tt.blurb}
            </span>
          </button>
        ))}
      </div>
    </section>
  );
}
